import React, { useState } from 'react';
import { useLogin, useNotify } from 'react-admin';
import ShsLogo from 'src/assets/ShsLogo';
import { ReactComponent as DiiaSolidLogo } from 'src/assets/Diia_Solid_Logo.svg';
import { ReactComponent as GoogleGLogo } from 'src/assets/Google Icon.svg';
import { Tab, Tabs } from '@mui/material';
import './index.css';
import QrCode from './QrCode';
import Restricted from '../common/Errors/Restricted';
import { useGoogleLogin } from '@react-oauth/google';
import { LocaleMapping } from 'src/constants';

enum LoginMethod {
    TEMPORARY = 'TEMPORARY',
    DIIA = 'DIIA',
    GOOGLE = 'GOOGLE'
}

interface GoogleAuthCodeResponse {
    authuser: string;
    code: string;
    prompt: string;
    scope: string;
}

const LoginPage: React.FC = () => {
    const [taxNumber, setTaxNumber] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [showQrCode, setShowQrCode] = useState<boolean>(false);
    const fakeAuthEnabled: boolean = process.env.REACT_APP_ENABLE_FAKE_AUTH === 'true';
    const [loginMethod, setLoginMethod] = useState<LoginMethod>(
        fakeAuthEnabled ? LoginMethod.TEMPORARY : LoginMethod.DIIA
    );
    const [restricted, setRestricted] = useState<boolean>(false);

    const login = useLogin();
    const notify = useNotify();

    const handleInputChange = (e: React.FormEvent<HTMLInputElement>) => setTaxNumber(e.currentTarget.value);

    const handlePasswordChange = (e: React.FormEvent<HTMLInputElement>) => setPassword(e.currentTarget.value);

    const handleShowQrCode = () => {
        setShowQrCode(true);
    };

    const handleSubmit = (e: React.SyntheticEvent) => {
        e.preventDefault();
        login({ taxNumber, password })
            .then(() => {
                localStorage.setItem('readNotifications', '');
            })
            .catch((e) => {
                const message = e.response.data.message;
                if (message === 'Користувач має оновити медичні дані') {
                    setRestricted(true);
                } else {
                    notify('Неправильний РНОКПП чи пароль');
                }
            });
    };

    const handleGoogleLoginSuccess = (codeResponse: { code: string }) => {
        login({ googleCode: codeResponse.code })
            .then(() => {
                localStorage.setItem('readNotifications', '');
            })
            .catch((e) => {
                notify('Помилка входу через Google');
            });
    };

    const googleLogin = useGoogleLogin({
        onSuccess: handleGoogleLoginSuccess,
        onError: () => {
            notify('Щось пішло не так під час Google Login');
        },
        flow: 'auth-code', // Requesting authorization code
        hint: LocaleMapping.ua // Added hint for Ukrainian
    });

    return (
        <React.Fragment>
            {restricted ? (
                <Restricted handleReturnLogin={() => setRestricted(false)} />
            ) : (
                <div className="page_container">
                    <div className="login_container">
                        <ShsLogo />
                        {fakeAuthEnabled && (
                            <Tabs
                                value={loginMethod}
                                onChange={(e, value) => {
                                    setShowQrCode(false);
                                    setLoginMethod(value);
                                }}>
                                <Tab value={LoginMethod.TEMPORARY} label="Тимчасовий вхід" />
                                <Tab value={LoginMethod.DIIA} label="Вхід" />
                                <Tab value={LoginMethod.GOOGLE} label="Вхід для працівників" />
                            </Tabs>
                        )}

                        {loginMethod === LoginMethod.TEMPORARY && fakeAuthEnabled && (
                            <div className="login_form">
                                <form onSubmit={handleSubmit} className="login_form_temporary" noValidate>
                                    <input
                                        className="login_form__input"
                                        name="taxNumbers"
                                        type="text"
                                        pattern="[0-9]{10}"
                                        placeholder="РНОКПП"
                                        value={taxNumber}
                                        onChange={handleInputChange}
                                    />
                                    <input
                                        className="login_form__input"
                                        name="password"
                                        type="password"
                                        placeholder="Пароль"
                                        value={password}
                                        onChange={handlePasswordChange}
                                    />
                                    <div className="login-button-container">
                                        <button className="login_form__action_button">Увійти</button>
                                    </div>
                                </form>
                                <button className="login__forgot_password">Забули пароль?</button>
                            </div>
                        )}

                        {loginMethod !== LoginMethod.TEMPORARY && !showQrCode && (
                            <div className="login_form">
                                <div className="login-diia-info">Авторизуйтесь для входу в кабінет</div>
                                <button className="login_form__action_button" onClick={handleShowQrCode}>
                                    <DiiaSolidLogo className="login_form__action_button__logo" /> Увійти через
                                    Дія.Підпис
                                </button>
                                {loginMethod === LoginMethod.GOOGLE && (
                                    <button className="login_form__action_button" onClick={() => googleLogin()}>
                                        <GoogleGLogo className="login_form__action_button__logo" /> Увійти через Google
                                    </button>
                                )}
                            </div>
                        )}

                        {[LoginMethod.DIIA, LoginMethod.GOOGLE].includes(loginMethod) && (
                            <QrCode showQrCode={showQrCode} />
                        )}
                    </div>
                </div>
            )}
        </React.Fragment>
    );
};

export default LoginPage;
